import type { FC } from "react"
import React, { useState } from "react"

import {
  autoPlacement,
  autoUpdate,
  offset,
  useFloating,
  useFloatingPortalNode,
} from "@floating-ui/react"
import { formatDistance } from "date-fns"
import { AnimatePresence, motion } from "framer-motion"
import ReactDOM from "react-dom"
import { useRecoilValue } from "recoil"
import styled from "styled-components"

import { proposalRemoteState } from "~/containers/Proposal/state/proposal/proposal-state"
import type { Design } from "~/graphql/client"
import { getMountTypeLabel } from "~/utils/mount-type"

import { ReactComponent as ClockIcon } from "@svg/clock.svg"
import { ReactComponent as TrashIcon } from "@svg/trash-icon.svg"

import { selectedModuleState } from "../state/elements/panels"

const getBackground = (isPreview: boolean, isRejected: boolean) => {
  if (isRejected) return `var(--grey-dark)`
  if (isPreview) return `var(--blue)`

  return `black`
}

export const StyledCard = styled.div<{
  isPreview: boolean
  isRejected: boolean
}>`
  width: 100%;
  height: 250px;
  border-radius: 4px;
  background: ${({ isPreview, isRejected }) =>
    getBackground(isPreview, isRejected)};
  cursor: ${({ isRejected }) => (isRejected ? `not-allowed` : `pointer`)};
  padding: var(--space-s);
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  transition: background 0.5s;
  :hover {
    > img {
      opacity: ${({ isRejected }) => (isRejected ? 0.35 : 0.1)};
    }
  }
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.5s;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    filter: ${({ isRejected }) => (isRejected ? `blur(5px)` : `none`)};
    opacity: ${({ isPreview }) => (isPreview ? 0.2 : 0.35)};
  }
  > div {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 5;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`
export const CardHeader = styled.div`
  display: flex;
  gap: var(--space-2xs);
  align-items: center;
  position: relative;
  > p {
    color: white;
    opacity: 0.75;
    font-size: var(--step--2);
    position: relative;
    z-index: 5;
  }
  > div {
    background-color: white;
    border-radius: 20px;
    color: var(--blue-dark-font);
    font-size: var(--step--1);
    font-weight: 600;
    padding: var(--space-3xs) var(--space-xs);
  }
  > svg {
    width: 22px;
    height: 22px;
    fill: white;
  }
`
export const CardBottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);
  position: relative;
  overflow: hidden;
  flex-grow: 0;
  width: 100%;
  > p {
    width: 100%;
    color: white;
    white-space: nowrap;
    font-size: var(--step--2);
    overflow: hidden;
    text-overflow: ellipsis;
  }
`
export const CardStats = styled.div`
  display: flex;
  gap: var(--space-xs);
  > div {
    border: 1px solid white;
    color: white;
    font-weight: 600;
    width: 50px;
    height: 60px;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  > main {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    p {
      color: white;
      font-weight: 600;
      font-size: var(--step--1);
    }
    > div {
      display: flex;
      gap: var(--space-xs);
      > div {
        background-color: white;
        border-radius: 4px;
        color: var(--blue-dark-font);
        font-size: var(--step--2);
        font-weight: 600;
        padding: var(--space-3xs) var(--space-xs);
        text-transform: capitalize;
      }
    }
  }
`

const Portal = ({
  show,
  children,
}: {
  show: boolean
  children: React.ReactNode
}) => {
  const portalNode = useFloatingPortalNode()

  if (portalNode && show) {
    return ReactDOM.createPortal(<motion.div>{children}</motion.div>, portalNode)
  }

  return null
}

export const Tooltip = styled(motion.div)`
  background-color: var(--blue);
  max-width: 400px;
  border-radius: 5px;
  overflow: hidden;
  padding: var(--space-2xs) var(--space-xs);
  height: fit-content;
  max-height: 40vh;
  color: white;
  font-size: var(--step--1);
  line-height: 1.25;
`

export const Card: FC<{
  design: Design
  index: number
  image: string
  isCurrent: boolean
  isPreview: boolean
  previewDesign: (id: string) => void
}> = ({ design, index, image, isCurrent, isPreview, previewDesign }) => {
  const proposal = useRecoilValue(proposalRemoteState)
  const selectedModule = useRecoilValue(selectedModuleState)
  const [show, setShow] = useState(false)
  const {
    x: x1,
    y: y1,
    reference,
    floating,
    strategy,
  } = useFloating<HTMLElement>({
    whileElementsMounted: autoUpdate,
    open: show,
    middleware: [
      autoPlacement({
        allowedPlacements: [`left`, `bottom`],
        rootBoundary: `viewport`,
      }),
      offset({
        mainAxis: 30,
      }),
    ],
  })
  const x = x1 ?? 0
  const y = y1 ?? 0

  const allNotes = design?.designRequest?.designNotes ?? []
  const note = allNotes[allNotes?.length - 1]?.message ?? ``
  const isRejected = design?.designRequest?.rejectedAt

  const setAsPreview = () => {
    if (isRejected) return
    previewDesign(design.id)
  }

  return (
    <StyledCard
      isPreview={isPreview}
      isRejected={isRejected}
      onClick={() => setAsPreview()}
    >
      <div>
        <CardHeader>
          {isCurrent && (
            <>
              <ClockIcon />
              <div>Current Version</div>
            </>
          )}
          {isRejected && (
            <>
              <TrashIcon />
              <div>Rejected</div>
            </>
          )}
          <p>
            {formatDistance(new Date(design.createdAt), new Date(), {
              addSuffix: true,
            })}
          </p>
        </CardHeader>
        <CardBottom>
          <CardStats>
            <div>{index < 10 ? `0${index}` : index}</div>
            <main>
              <p>{selectedModule.name}</p>
              <div>
                <div>{getMountTypeLabel(proposal.systemType)}</div>
              </div>
            </main>
          </CardStats>
          <p
            onMouseOver={() => setShow(true)}
            onMouseOut={() => setShow(false)}
            ref={reference}
          >
            {note}
          </p>
          <AnimatePresence>
            <Portal show={show}>
              <Tooltip
                style={{
                  left: x,
                  top: y,
                  position: strategy,
                  zIndex: 999999,
                }}
                ref={floating}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, delay: 0.25 }}
              >
                {note}
              </Tooltip>
            </Portal>
          </AnimatePresence>
        </CardBottom>
      </div>
      <img src={image || ``} alt="imagery" />
    </StyledCard>
  )
}
